import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Flag, Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorState,
  LoadingState,
  StatusBadge,
  type Tone,
  cn,
} from "../../shared/ui";
import type { MilestoneView } from "../../shared/types/api";
import { AchieveMilestoneDrawer, MilestoneFormDrawer } from "./MilestoneDrawers";
import { formatSlippage } from "./projectVocabulary";
import {
  achievementNote,
  countdownTone,
  daysUntil,
  formatCountdown,
  journey,
  orderMilestones,
  today,
} from "./milestoneCountdown";

const STATUS_LABELS: Record<MilestoneView["status"], string> = {
  PLANNED: "Planned",
  IN_PROGRESS: "In progress",
  ACHIEVED: "Achieved",
  MISSED: "Missed",
  CANCELLED: "Cancelled",
};

const STATUS_TONE: Record<MilestoneView["status"], Tone> = {
  PLANNED: "gray",
  IN_PROGRESS: "blue",
  ACHIEVED: "green",
  MISSED: "red",
  CANCELLED: "gray",
};

/**
 * Dated commitments, and how far they have moved (§105).
 *
 * <p>Three dates are shown side by side rather than one: what was committed at baseline, what the
 * plan says now, and what is forecast. Collapsing them into a single date would show a project
 * that had slipped twice as one that never moved at all.
 *
 * <p>Read top to bottom as a sequence, because that is what a run of dated commitments is. The
 * countdown on the next one is the largest thing on the page: of everything here, it is the only
 * figure that changes on its own, and the only one anybody can still do something about.
 *
 * <p>Reaching a date is marked as plainly as missing one. The page previously recorded slippage
 * and said nothing at all about a milestone hit early, which made the whole tab read as a ledger
 * of failures — but nothing here softens a miss to achieve that. Late stays late, and stays red.
 */
export function ProjectMilestonesTab({ projectId }: { projectId: string }) {
  const { can } = useAuth();
  const [editing, setEditing] = useState<MilestoneView | null>(null);
  const [creating, setCreating] = useState(false);
  const [achieving, setAchieving] = useState<MilestoneView | null>(null);

  const edit = can("PROJECT_EDIT");

  const milestones = useQuery({
    queryKey: ["milestones", projectId],
    queryFn: () => api.get<MilestoneView[]>(`/api/v1/projects/${projectId}/milestones`),
  });

  if (milestones.isPending) {
    return <LoadingState label="Loading milestones…" />;
  }
  if (milestones.isError) {
    return <ErrorState error={milestones.error} onRetry={() => void milestones.refetch()} />;
  }

  const reload = () => void milestones.refetch();
  const now = today();
  const stats = journey(milestones.data);
  const ordered = orderMilestones(milestones.data);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-ink-500">
          Each milestone keeps the date it was committed to at baseline alongside the current plan,
          so slippage stays visible rather than being re-planned away.
        </p>
        {edit && milestones.data.length > 0 && (
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
            Add milestone
          </Button>
        )}
      </div>

      {milestones.data.length === 0 ? (
        <Card>
          <EmptyState
            title="No milestones yet"
            message="Milestones are the dated commitments a project is judged on — a demo, a go-live, a phase sign-off."
            action={
              edit && (
                <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
                  Add the first milestone
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <>
          <JourneyHeader stats={stats} now={now} />

          {/*
            A spine down the left, with each milestone hanging off it. The marker carries the
            state, so the eye can run down the line and see how far the project has got without
            reading a single date.
          */}
          <ol className="relative mt-4">
            {ordered.map((milestone, index) => (
              <li key={milestone.id} className="relative flex gap-4">
                <div className="relative flex w-3 shrink-0 justify-center">
                  <Marker milestone={milestone} isNext={stats.next?.id === milestone.id} />
                  {index < ordered.length - 1 && (
                    <span
                      className="absolute top-2 bottom-0 w-px bg-ink-200"
                      aria-hidden
                    />
                  )}
                </div>

                <div className="min-w-0 flex-1 pb-3">
                  <MilestoneCard
                    milestone={milestone}
                    isNext={stats.next?.id === milestone.id}
                    now={now}
                    canEdit={edit}
                    onEdit={() => setEditing(milestone)}
                    onAchieve={() => setAchieving(milestone)}
                  />
                </div>
              </li>
            ))}
          </ol>
        </>
      )}

      <MilestoneFormDrawer
        projectId={projectId}
        milestone={editing}
        open={creating || editing !== null}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={reload}
      />
      <AchieveMilestoneDrawer
        projectId={projectId}
        milestone={achieving}
        onClose={() => setAchieving(null)}
        onSaved={reload}
      />
    </>
  );
}

/**
 * How far the project has got, in one strip.
 *
 * <p>The count leads rather than the percentage. "3 of 7" is a thing somebody can picture; a bar
 * at 43% is a statistic about a thing they cannot.
 */
function JourneyHeader({
  stats,
  now,
}: {
  stats: ReturnType<typeof journey>;
  now: string;
}) {
  const finished = stats.total > 0 && stats.reached === stats.total;

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
          <p className="text-sm text-ink-600">
            <span className="font-display text-xl text-ink-900">{stats.reached}</span>
            <span className="text-ink-400"> of </span>
            <span className="font-display text-xl text-ink-900">{stats.total}</span>
            <span> reached</span>
            {stats.onTime > 0 && (
              <span className="text-ink-500">
                {" · "}
                {stats.onTime} on time or early
              </span>
            )}
          </p>

          {stats.next ? (
            <p className="text-sm text-ink-500">
              Next up{" "}
              <span className="font-medium text-ink-900">{stats.next.name}</span>
            </p>
          ) : finished ? (
            <p className="text-sm font-medium text-emerald-600">Every milestone reached.</p>
          ) : null}
        </div>

        {/*
          Drawn from the counts rather than the ProgressBar component, which fills in one tone.
          The point of the bar here is that a milestone reached late still counts as reached —
          the project moved — so both greens sit in the same fill.
        */}
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-ink-100"
          role="progressbar"
          aria-valuenow={stats.percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Milestones reached"
        >
          <div
            className="h-full rounded-full bg-emerald-500 transition-[width] duration-500"
            style={{ width: `${stats.percent}%` }}
          />
        </div>

        {stats.next && <NextCountdown milestone={stats.next} now={now} />}
      </CardBody>
    </Card>
  );
}

/** The one figure on the page worth setting large: how long is left. */
function NextCountdown({ milestone, now }: { milestone: MilestoneView; now: string }) {
  const target = milestone.forecastDate ?? milestone.currentDate;
  const days = daysUntil(target, now);
  const tone = countdownTone(days);

  return (
    <div className="flex items-baseline gap-2 pt-1">
      <span
        className={cn(
          "font-display text-3xl leading-none",
          tone === "overdue"
            ? "text-red-600"
            : tone === "near"
              ? "text-amber-600"
              : "text-brand-600",
        )}
      >
        {Math.abs(days) === 0 ? "today" : Math.abs(days)}
      </span>
      <span className="text-sm text-ink-500">
        {days === 0
          ? `— ${milestone.name} is due`
          : days > 0
            ? `${days === 1 ? "day" : "days"} until ${milestone.name}`
            : `${Math.abs(days) === 1 ? "day" : "days"} overdue — ${milestone.name}`}
      </span>
    </div>
  );
}

/** The dot on the spine. Colour is the state; a ring means this is the one in front of you. */
function Marker({ milestone, isNext }: { milestone: MilestoneView; isNext: boolean }) {
  const reached = milestone.status === "ACHIEVED";
  const missed = milestone.status === "MISSED";

  return (
    <span
      className={cn(
        "relative z-10 mt-1.5 h-3 w-3 shrink-0 rounded-full border-2",
        reached
          ? "border-emerald-500 bg-emerald-500"
          : missed
            ? "border-red-500 bg-red-500"
            : isNext
              ? "border-brand-500 bg-surface ring-4 ring-brand-50"
              : "border-ink-300 bg-surface",
      )}
      aria-hidden
    />
  );
}

function MilestoneCard({
  milestone,
  isNext,
  now,
  canEdit,
  onEdit,
  onAchieve,
}: {
  milestone: MilestoneView;
  isNext: boolean;
  now: string;
  canEdit: boolean;
  onEdit: () => void;
  onAchieve: () => void;
}) {
  const late = milestone.slippageDays > 0;
  const reached = milestone.status === "ACHIEVED";
  const note = achievementNote(milestone);
  const open = milestone.status !== "ACHIEVED" && milestone.status !== "CANCELLED";
  const days = daysUntil(milestone.forecastDate ?? milestone.currentDate, now);
  const tone = countdownTone(days);

  return (
    <Card className={cn(isNext && "border-brand-200", reached && "bg-emerald-50/40")}>
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {reached ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
              ) : (
                <Flag className="h-4 w-4 shrink-0 text-ink-400" />
              )}
              <h3 className="font-medium text-ink-900">{milestone.name}</h3>
              <StatusBadge tone={STATUS_TONE[milestone.status]}>
                {STATUS_LABELS[milestone.status]}
              </StatusBadge>
              {note && (
                <span
                  className={cn(
                    "text-sm font-medium",
                    note.tone === "good" ? "text-emerald-600" : "text-red-600",
                  )}
                >
                  {note.text}
                </span>
              )}
              {milestone.ownerName && (
                <span className="text-sm text-ink-500">· {milestone.ownerName}</span>
              )}
            </div>
            {milestone.description && (
              <p className="mt-1 text-sm text-ink-500">{milestone.description}</p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-3">
            {open && (
              <span
                className={cn(
                  "text-sm font-medium whitespace-nowrap",
                  tone === "overdue"
                    ? "text-red-600"
                    : tone === "near"
                      ? "text-amber-600"
                      : "text-ink-500",
                )}
              >
                {formatCountdown(days)}
              </span>
            )}

            {canEdit && (
              <div className="flex gap-1.5">
                {milestone.status !== "ACHIEVED" && (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<CheckCircle2 className="h-3.5 w-3.5" />}
                    onClick={onAchieve}
                  >
                    Achieved
                  </Button>
                )}
                <Button variant="ghost" size="sm" title="Edit milestone" onClick={onEdit}>
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-4 border-t border-ink-100 pt-3 sm:grid-cols-5">
          <DateFigure label="Committed" value={milestone.baselineDate} empty="not baselined" />
          <DateFigure label="Planned" value={milestone.currentDate} />
          <DateFigure label="Forecast" value={milestone.forecastDate} empty="—" />
          <DateFigure label="Achieved" value={milestone.actualDate} empty="—" />
          <div>
            <p className="text-xs text-ink-500">Slippage</p>
            <p
              className={cn(
                "tnum mt-0.5 text-sm font-medium",
                !milestone.baselineDate
                  ? "text-ink-400"
                  : late
                    ? "text-red-600"
                    : "text-emerald-600",
              )}
            >
              {milestone.baselineDate ? formatSlippage(milestone.slippageDays) : "not measurable"}
            </p>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function DateFigure({
  label,
  value,
  empty = "—",
}: {
  label: string;
  value: string | null;
  empty?: string;
}) {
  return (
    <div>
      <p className="text-xs text-ink-500">{label}</p>
      <p className={cn("tnum mt-0.5 text-sm", value ? "text-ink-900" : "text-ink-400")}>
        {value ?? empty}
      </p>
    </div>
  );
}
